/**
 * The pull & pack list (the Oct 21 canvas, "Proposed · p.16 · Pull & pack
 * list"): every ready-made item of the day, the food the kitchen pulls from
 * stock rather than cooks, a section per meal (today's first, a later day's
 * "ride-along" last), then Milk, then Condiments.
 *
 * - A ready-made item is a deliverable with nothing to cook (no recipe with
 *   steps: a fruit cup, a juice, a cookie), or a ready-made food packed inside
 *   a deliverable the kitchen cooks for ("Dinner Roll · In BBQ Filet combo
 *   container"). Cooked food has its recipe page (`recipes.ts`).
 * - A condiment that ships on its own, not packed inside a unit, has its own
 *   Condiments section at the end; one packed in a container stays with its
 *   meal ("Mayo + Mustard Packet · In House Salad container").
 * - Five stops or fewer: the per-stop grid, a column per stop by number and
 *   code ("1 OPM"), a key to the codes under the title. Six or more: the Pull
 *   List, totals only, to pack stop by stop from the kitchen stop sheets
 *   (the canvas: portrait letter holds about eight stop columns; five leaves
 *   room for long names).
 *
 * Every temperature is the kitchen's (`deliverTemperature`). Pure;
 * unit-tested.
 */

import { deliverableMode } from '@/lib/chefs/deliverable-copy'
import { deliverableTypeGroup } from '@/lib/chefs/pack-groups'
import type { Component, Deliverable, Temperature } from '@/lib/chefs/types'
import { MEALTIME_LABEL } from '@/lib/chefs/types'

import { KITCHEN_COPY, type PacketDay } from './day'
import { serveDayWords } from './meals'
import { isPrepComponent } from './recipes'
import { deliverTemperature } from './rows'
import type { PacketStop } from './stops'
import { mealsAcrossStops, todayThenLater } from './summaries'
import type { PacketSection, PacketUnit } from './units'

/** Stop columns the grid holds; past this the page is the totals-only Pull List. */
export const PULL_GRID_MAX_STOPS = 5

/** A deliverable that is all condiments and ships on its own: the Condiments section's. */
export function isCondimentDeliverable(d: Pick<Deliverable, 'productType' | 'components'>): boolean {
  return deliverableTypeGroup(d) === 'condiments'
}

/** A row of the list before it is laid out: one item, one way of packing it, its servings at each stop. */
interface PullItem {
  key: string
  name: string
  pack: string | null
  temperature: Temperature | null
  counts: Array<number | null>
  total: number
}

/** A deliverable's own packing, in words: "Bulk delivery box", "Kraft sandwich bag"; null when it names none. */
function packWords(d: Pick<Deliverable, 'packaging'>): string | null {
  return d.packaging?.trim() || null
}

/** How many of a ready-made food a deliverable holds: one per serving, a condiment its count per serving. */
function componentQty(d: Deliverable, c: Component): number {
  return c.isCondiment ? d.qty * (c.condimentQty ?? 1) : d.qty
}

/** The words a stop's column is named by: its site, led by its organization's first words when the site is short. */
function stopWords(stop: Pick<PacketStop, 'identity'>): string[] {
  const words = (text: string) => text.split(/\s+/).filter((word) => /[\p{L}\p{N}]/u.test(word))
  const site = words(stop.identity.site)
  return site.length >= 3 ? site : [...words(stop.identity.organization ?? '').slice(0, 3 - site.length), ...site]
}

/** "Huda Lower Campus" for Huda's Lower Campus: the grid key's name for a stop. */
export function stopKeyName(stop: Pick<PacketStop, 'identity'>): string {
  return stopWords(stop).join(' ')
}

/** "OPM" for "OLHSA Pontiac Main Building": the first letters of the first three words of the stop's name. */
export function stopCode(stop: Pick<PacketStop, 'identity'>): string {
  return stopWords(stop)
    .slice(0, 3)
    .map((word) =>
      word
        .replace(/[^\p{L}\p{N}]/gu, '')
        .charAt(0)
        .toUpperCase(),
    )
    .join('')
}

/** Every stop's code, a repeat numbered so no two columns read alike ("HLC", "HLC2"). */
export function stopCodes(stops: ReadonlyArray<Pick<PacketStop, 'identity'>>): string[] {
  const seen = new Map<string, number>()
  return stops.map((stop) => {
    const code = stopCode(stop)
    const n = (seen.get(code) ?? 0) + 1
    seen.set(code, n)
    return n === 1 ? code : `${code}${n}`
  })
}

function addItem(
  items: Map<string, PullItem>,
  stopCount: number,
  stopIndex: number,
  seed: Omit<PullItem, 'key' | 'counts' | 'total'>,
  qty: number,
) {
  const key = `${seed.name}|${seed.pack ?? ''}|${seed.temperature ?? ''}`
  let item = items.get(key)
  if (!item) {
    item = { key, ...seed, counts: Array.from({ length: stopCount }, () => null), total: 0 }
    items.set(key, item)
  }
  item.counts[stopIndex] = (item.counts[stopIndex] ?? 0) + qty
  item.total += qty
}

/** The ready-made items of one deliverable: itself when nothing in it is cooked, else its ready-made foods. */
function readyMade(d: Deliverable): Array<{ name: string; pack: string | null; qty: number }> {
  if (!d.components.some(isPrepComponent)) {
    return [{ name: d.name, pack: packWords(d), qty: d.qty }]
  }
  const vessel = deliverableMode(d) === 'bulk' ? 'With' : 'In'
  return d.components
    .filter((c) => !isPrepComponent(c))
    .map((c) => ({
      name: c.name,
      pack: vessel === 'In' ? `In ${d.name} container` : `With ${d.name}`,
      qty: componentQty(d, c),
    }))
}

/** "Breakfast · ride-along" for a later day's meal, else the meal. */
function mealTitle(mealtime: keyof typeof MEALTIME_LABEL, later: boolean): string {
  const word = MEALTIME_LABEL[mealtime].replace(' Snack', ' snack')
  return later ? `${word} · ride-along` : word
}

export interface PullGroup {
  key: string
  title: string
  /** "Serve today", "Serve Mon 10/19"; null for Milk and Condiments. */
  note: string | null
  items: PullItem[]
}

/** The day's pull groups in print order: each meal with ready-made food, Milk, then Condiments. */
export function pullGroups(day: Pick<PacketDay, 'stops' | 'iso'>): PullGroup[] {
  const stopCount = day.stops.length
  const groups: PullGroup[] = []
  const condiments = new Map<string, PullItem>()
  for (const meal of todayThenLater(mealsAcrossStops(day))) {
    const items = new Map<string, PullItem>()
    meal.perStop.forEach((section, i) => {
      for (const d of section?.block.deliverables ?? []) {
        if (isCondimentDeliverable(d) && !d.components.some(isPrepComponent)) {
          addItem(
            condiments,
            stopCount,
            i,
            { name: d.name, pack: packWords(d), temperature: deliverTemperature(d) },
            d.qty,
          )
          continue
        }
        for (const item of readyMade(d)) {
          addItem(
            items,
            stopCount,
            i,
            { name: item.name, pack: item.pack, temperature: deliverTemperature(d) },
            item.qty,
          )
        }
      }
    })
    if (items.size > 0) {
      groups.push({
        key: meal.key,
        title: mealTitle(meal.mealtime, meal.serve.later),
        note: meal.serve.later && meal.serve.laterDay ? `Serve ${serveDayWords(meal.serve.laterDay)}` : 'Serve today',
        items: [...items.values()],
      })
    }
  }
  const milk = new Map<string, PullItem>()
  day.stops.forEach((stop, i) => {
    for (const line of stop.campus.milk ?? []) {
      addItem(
        milk,
        stopCount,
        i,
        { name: line.name, pack: line.packaging?.trim() || null, temperature: line.temperature },
        line.qty,
      )
    }
  })
  if (milk.size > 0) {
    groups.push({ key: 'milk', title: 'Milk', note: null, items: [...milk.values()] })
  }
  if (condiments.size > 0) {
    groups.push({ key: 'condiments', title: 'Condiments', note: null, items: [...condiments.values()] })
  }
  return groups
}

/** The number of rows the list prints: the cover's "14 pull items". */
export function pullItemCount(groups: readonly PullGroup[]): number {
  return groups.reduce((sum, group) => sum + group.items.length, 0)
}

export function pullPackSectionId(dayIso: string): string {
  return `${dayIso}|pull-pack`
}

/**
 * The day's pull & pack list (the grid to five stops, the Pull List past them); null when nothing is ready-made.
 * `sheetIds` are the stop sheets the Pull List sends the packer to, for their pages.
 */
export function pullPackSection(
  day: PacketDay,
  groups: readonly PullGroup[],
  sheetIds: readonly string[],
): PacketSection | null {
  if (groups.length === 0) {
    return null
  }
  const sid = pullPackSectionId(day.iso)
  const grid = day.stops.length <= PULL_GRID_MAX_STOPS
  const codes = stopCodes(day.stops)
  const columns = grid
    ? day.stops.map((stop, i) => ({ number: stop.identity.number, code: codes[i]!, site: stopKeyName(stop) }))
    : null
  const units: PacketUnit[] = [
    grid
      ? { id: `${sid}|key`, group: null, role: 'block', kind: 'pull-key', stops: columns! }
      : { id: `${sid}|intro`, group: null, role: 'block', kind: 'pull-intro', sheetIds: [...sheetIds] },
  ]
  for (const group of groups) {
    const gid = `${sid}|${group.key}`
    units.push(
      { id: `${gid}|band`, group: gid, role: 'band', kind: 'band', text: group.title, note: group.note, level: 1 },
      { id: `${gid}|head`, group: gid, role: 'band', kind: 'pull-head', stops: columns },
      ...group.items.map(
        (item): PacketUnit => ({
          id: `${gid}|${item.key}`,
          group: gid,
          role: 'row',
          kind: 'pull-row',
          name: item.name,
          pack: item.pack,
          temperature: item.temperature,
          counts: grid ? item.counts : null,
          total: item.total,
        }),
      ),
    )
  }
  const title = grid ? 'Pull & Pack List' : 'Pull List'
  return {
    id: sid,
    kind: 'pull-pack',
    dayIso: day.iso,
    band: { name: 'PULL & PACK', counter: null, right: KITCHEN_COPY, style: 'solid' },
    title: {
      eyebrow: grid
        ? 'READY-MADE ITEMS · NO PREP · SERVINGS BY STOP'
        : `READY-MADE ITEMS · NO PREP · ALL ${day.stops.length} STOPS`,
      title,
      note: null,
      dateBox: day.dateBox,
    },
    footer: { key: `${day.iso}|pull-pack`, label: title, counted: false },
    units,
    repeat: [],
    preferOnePage: false,
  }
}
